import { useSelectionStore } from '@/shared/state/selection'
import type { TimelineItem } from '@/types/timeline'
import { useEditorStore } from '@/app/state/editor'
import { isFrameInTransitionRegion } from '@/features/timeline/deps/keyframes'
import { useItemsStore } from '../items-store'
import { useKeyframesStore } from '../keyframes-store'
import { useTimelineSettingsStore } from '../timeline-settings-store'
import { useTransitionsStore } from '../transitions-store'
import { applyTransitionRepairs, execute, warnIfOverlapping } from './shared'
import { splitItemAtFrames } from './item-edit-actions'
import { buildLinkedLeftShiftUpdates, expandIdsWithLinkedItems } from './linked-edit'
import { getLinkedItems } from '../../utils/linked-items'
import type { SilenceRange, SilenceRangesByItemId } from '../../utils/silence-removal-preview'

export interface ApplySilenceRemovalResult {
  processedItemCount: number
  skippedItemCount: number
  skippedItemIds: string[]
  removedSegmentCount: number
  shiftedItemCount: number
  totalRemovedFrames: number
}

function isLinkedSelectionEnabled(): boolean {
  return useEditorStore.getState().linkedSelectionEnabled
}

function getItemFamilyOriginId(item: TimelineItem): string {
  return item.originId ?? item.id
}

function normalizeRanges(ranges: readonly SilenceRange[], durationInFrames: number): SilenceRange[] {
  return ranges
    .map((range) => ({
      ...range,
      startFrame: Math.max(0, Math.min(durationInFrames, range.startFrame)),
      endFrame: Math.max(0, Math.min(durationInFrames, range.endFrame)),
    }))
    .filter((range) => range.endFrame > range.startFrame)
    .sort((left, right) => left.startFrame - right.startFrame)
}

function canApplySplitFrames(anchorItemId: string, splitFrames: readonly number[]): boolean {
  if (splitFrames.length === 0) {
    return true
  }

  const items = useItemsStore.getState().items
  const transitions = useTransitionsStore.getState().transitions
  const itemsToSplit = isLinkedSelectionEnabled() ? getLinkedItems(items, anchorItemId) : []
  const anchorItem = items.find((item) => item.id === anchorItemId)
  const targets = itemsToSplit.length > 0 ? itemsToSplit : anchorItem ? [anchorItem] : []

  return targets.every((item) =>
    splitFrames.every((frame) => {
      if (frame <= item.from || frame >= item.from + item.durationInFrames) {
        return false
      }

      return isFrameInTransitionRegion(frame, item.id, item, transitions) === undefined
    }),
  )
}

export function applySilenceRemovalToItems(
  itemIds: readonly string[],
  rangesByItemId: SilenceRangesByItemId,
): ApplySilenceRemovalResult {
  return applyRangeRemovalToItems('APPLY_SILENCE_REMOVAL', itemIds, rangesByItemId)
}

export function applyFillerRemovalToItems(
  itemIds: readonly string[],
  rangesByItemId: SilenceRangesByItemId,
): ApplySilenceRemovalResult {
  return applyRangeRemovalToItems('APPLY_FILLER_REMOVAL', itemIds, rangesByItemId)
}

function applyRangeRemovalToItems(
  commandType: 'APPLY_SILENCE_REMOVAL' | 'APPLY_FILLER_REMOVAL',
  itemIds: readonly string[],
  rangesByItemId: SilenceRangesByItemId,
): ApplySilenceRemovalResult {
  const linkedSelectionEnabled = isLinkedSelectionEnabled()
  const processedOriginIds = new Set<string>()
  const changedItemIds = new Set<string>()
  const removeIds = new Set<string>()
  const moveUpdatesById = new Map<string, { id: string; from: number }>()
  const finalSelectionIds = new Set<string>()

  let processedItemCount = 0
  let skippedItemCount = 0
  const skippedItemIds: string[] = []
  let removedSegmentCount = 0
  let totalRemovedFrames = 0

  for (const itemId of itemIds) {
    const anchorItem = useItemsStore.getState().itemById[itemId]
    if (!anchorItem || (anchorItem.type !== 'audio' && anchorItem.type !== 'video')) {
      skippedItemCount += 1
      skippedItemIds.push(itemId)
      continue
    }

    const familyOriginId = getItemFamilyOriginId(anchorItem)
    if (processedOriginIds.has(familyOriginId)) {
      continue
    }
    processedOriginIds.add(familyOriginId)

    const normalizedRanges = normalizeRanges(rangesByItemId[itemId] ?? [], anchorItem.durationInFrames)
    if (normalizedRanges.length === 0) {
      skippedItemCount += 1
      skippedItemIds.push(itemId)
      continue
    }

    const splitFrames = Array.from(
      new Set(
        normalizedRanges
          .flatMap((range) => [range.startFrame, range.endFrame])
          .map((frame) => anchorItem.from + frame),
      ),
    ).filter((frame) => frame > anchorItem.from && frame < anchorItem.from + anchorItem.durationInFrames)

    if (!canApplySplitFrames(itemId, splitFrames)) {
      skippedItemCount += 1
      skippedItemIds.push(itemId)
      continue
    }

    if (splitFrames.length > 0) {
      splitItemAtFrames(itemId, splitFrames)
    }

    const currentItems = useItemsStore.getState().items
    const absoluteSilentRanges = normalizedRanges.map((range) => ({
      start: anchorItem.from + range.startFrame,
      end: anchorItem.from + range.endFrame,
      duration: range.endFrame - range.startFrame,
    }))

    const familyItems = currentItems.filter((item) => getItemFamilyOriginId(item) === familyOriginId)
    const familySilentIds = familyItems
      .filter((item) =>
        absoluteSilentRanges.some(
          (range) => item.from === range.start && item.from + item.durationInFrames === range.end,
        ),
      )
      .map((item) => item.id)

    const expandedRemoveIds = expandIdsWithLinkedItems(currentItems, familySilentIds, linkedSelectionEnabled)
    if (expandedRemoveIds.length === 0) {
      skippedItemCount += 1
      skippedItemIds.push(itemId)
      continue
    }

    processedItemCount += 1
    removedSegmentCount += familySilentIds.length
    totalRemovedFrames += absoluteSilentRanges.reduce((sum, range) => sum + range.duration, 0)

    for (const id of expandedRemoveIds) {
      removeIds.add(id)
      changedItemIds.add(id)
    }

    const remainingFamilyItems = familyItems.filter((item) => !removeIds.has(item.id))
    const baseShiftByItemId = new Map<string, number>()
    for (const item of remainingFamilyItems) {
      const shiftAmount = absoluteSilentRanges
        .filter((range) => range.end <= item.from)
        .reduce((sum, range) => sum + range.duration, 0)
      if (shiftAmount > 0) {
        baseShiftByItemId.set(item.id, shiftAmount)
      }
    }

    const moveUpdates = buildLinkedLeftShiftUpdates(currentItems, baseShiftByItemId, linkedSelectionEnabled)
    for (const update of moveUpdates) {
      if (removeIds.has(update.id)) {
        continue
      }
      moveUpdatesById.set(update.id, update)
      changedItemIds.add(update.id)
      finalSelectionIds.add(update.id)
    }

    for (const item of remainingFamilyItems) {
      if (!removeIds.has(item.id)) {
        finalSelectionIds.add(item.id)
      }
    }
  }

  if (removeIds.size === 0 && moveUpdatesById.size === 0) {
    return {
      processedItemCount,
      skippedItemCount,
      skippedItemIds,
      removedSegmentCount,
      shiftedItemCount: 0,
      totalRemovedFrames,
    }
  }

  const finalMoveUpdates = [...moveUpdatesById.values()]
  const removedIds = [...removeIds]

  execute(
    commandType,
    () => {
      useItemsStore.getState()._removeItems(removedIds)
      if (finalMoveUpdates.length > 0) {
        useItemsStore.getState()._moveItems(finalMoveUpdates)
      }

      useTransitionsStore.getState()._removeTransitionsForItems(removedIds)
      useKeyframesStore.getState()._removeKeyframesForItems(removedIds)
      applyTransitionRepairs([...changedItemIds], new Set(removedIds))
      useSelectionStore.getState().selectItems([...finalSelectionIds])
      useTimelineSettingsStore.getState().markDirty()
      warnIfOverlapping('applySilenceRemoval')
    },
    {
      itemCount: itemIds.length,
      removedCount: removedIds.length,
      shiftedCount: finalMoveUpdates.length,
      totalRemovedFrames,
    },
  )

  return {
    processedItemCount,
    skippedItemCount,
    skippedItemIds,
    removedSegmentCount,
    shiftedItemCount: finalMoveUpdates.length,
    totalRemovedFrames,
  }
}